import { Directive, OnDestroy, OnInit } from '@angular/core';
import { audit, BehaviorSubject, combineLatest, debounceTime, distinctUntilChanged, Observable, Subject, Subscription, switchMap, takeUntil, tap, throttleTime, timer } from 'rxjs';
import { ResultSet } from '../models/result-set.model';
import { Base } from '../models/base.model';
import { ApiService } from '../services/api.service';
import { ColumnConfig } from '../models/column-config.model';
import { AsyncValidatorFn, FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TitleService } from '../services/title.service';
import { NotificationService } from '../services/notification.service';
import { PageStateManagerService } from '../services/page-state-manager.service';
import { AuthService } from '../services/auth.service';
import { User } from '../../modules/admin/models/user.model';


@Directive()
export abstract class DataTableDirective<T extends Base> implements OnInit, OnDestroy {
  resultSet$: Observable<ResultSet<T>> | null = null;
  resultSet: ResultSet<T> | null = null;
  abstract columns: ColumnConfig[];
  abstract modelName: string;


  protected currentPageSubject = new BehaviorSubject<number>(1);
  protected pageSizeSubject = new BehaviorSubject<number>(10);
  protected searchTermSubject = new BehaviorSubject<string>('');
  private editStatusSubscription: Subscription | null = null;
  private columnsForCreateCache: ColumnConfig[] | null = null;
  private destroy$ = new Subject<void>();

  currentPage: number = 1;
  pageSize: number = 10;
  totalPages: number = 0;
  totalItems: number = 0;
  loading: boolean = false;
  editForms: { [key: string]: FormGroup } = {};
  editItemID: number | null = null;
  deleteItemID: number | null = null;
  showConfirmDialog = false;
  confirmMessage = '';
  currentValidationErrors: { columnLabel: string; message: string }[] = [];
  showModal = false;
  modalTitle = '';
  user: User | null = null;

  constructor(
    protected apiService: ApiService<T>,
    private fb: FormBuilder,
    protected route: ActivatedRoute,
    protected router: Router,
    private titleService: TitleService,
    protected notificationService: NotificationService,
    protected pageStateManager: PageStateManagerService,
    protected authService: AuthService,
  ) {}

  onCreateStart(): void {
      this.editItemID = null;
      this.showModal = true;
  }

  ngOnInit() {
    const currentPage = this.pageStateManager.getPageState(this.router.url);
    this.currentPage = currentPage ? +currentPage : 1;
    this.currentPageSubject = new BehaviorSubject<number>(this.currentPage);

    this.loading = true; // Show loading state
    this.titleService.setTitle(`${this.modelName}s`);
    this.resultSet$ = this.buildResultSet$();
    this.resultSet$.subscribe({
      next: (resultSet) => {
        this.totalItems = resultSet.total;
        this.totalPages = Math.ceil(this.totalItems / this.pageSize);
        this.currentPage = resultSet.page;
        this.resultSet = resultSet;
        this.initializeForms();
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading data:', err);
        this.loading = false;
      }
    });
    this.authService.user$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.user = user;
      });
  }

  protected buildResultSet$(): Observable<ResultSet<T>> {
    return combineLatest([
      this.currentPageSubject,
      this.pageSizeSubject.pipe(distinctUntilChanged()),
      this.searchTermSubject.pipe(distinctUntilChanged())
    ]).pipe(
      throttleTime(300, undefined, { leading: true, trailing: true }),
      switchMap(([page, size, search]) => {
        const offset = (page - 1) * size;
        const request$ = search
        ? this.apiService.search(search, offset, size)
        : this.apiService.getAll(offset, size);
        return request$;
      })
    );
  }

  get columnsForCreate(): ColumnConfig[] {
    if(this.columnsForCreateCache === null) {
      this.columnsForCreateCache = this.getColumnsForCreate();
    }
    return this.columnsForCreateCache;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
  }

  getValueForInput(item: T, column: ColumnConfig): any {
      const rawValue = this.getNestedValue(item, column.name);
      if (column.inputType === 'date' && rawValue) {
        const date = new Date(rawValue);
        return date.toISOString().split('T')[0];
      }
      if(column.inputType === 'checkbox' && rawValue == null) {
        return false; // Default to false if checkbox value is null
      }
      return rawValue;
  }

  getDisplayValueFn = (item: T, column: ColumnConfig) => {
      const columnName = column.displayColumn ?? column.name
      return this.getNestedValue(item, columnName);
  }

  generateLinkFn = (routeTemplate: string[], item: T, column: ColumnConfig): string[] => {
    const link =  routeTemplate.map(segment =>
      segment == ':param'
        ? this.getNestedValue(item, column.name)
        : segment
    );
    return link;
  }

  initializeForms() {
    if (!this.resultSet) {
      return;
    }
    this.editForms = {};
    const columns = this.getColumnsForEdit()
    this.resultSet.data.forEach(item => {
      if (!item.id) {
        return;
      }
      const rowForm = this.fb.group({});
      columns.forEach(column => {
        const syncValidators = column.validators ?? [];
        rowForm.addControl(
          column.name,
          this.fb.control(
            this.getValueForInput(item, column),
            { updateOn: column.updateOn ?? 'change',
              validators: syncValidators
            }
          )
        );
      });
      this.editForms[item.id] = rowForm;
    });
  }

  // Override this method in child classes to provide async validators for edit forms
  // This allows for custom validation logic based on the item being edited
  protected getAsyncValidatorsForEdit(column: ColumnConfig, itemId: number): AsyncValidatorFn[] {
    return column.asyncValidators ?? [];
  }

  protected getColumnsForEdit(): ColumnConfig[] {
    return this.columns
  }

  protected getColumnsForCreate(): ColumnConfig[] {
    return this.columns.filter(column => column.inputType !== 'link');
  }

  onEditStart(itemID: number) {
    this.editItemID = itemID;
    const formGroup = this.editForms[itemID];
    if (formGroup) {
      this.cleanupEditSubscription();
      this.getColumnsForEdit().forEach(column => {
        const control = formGroup.get(column.name);
        const asyncValidators = this.getAsyncValidatorsForEdit(column, itemID!);
        if (control && asyncValidators.length > 0) {
          control.setAsyncValidators(asyncValidators);
          control.updateValueAndValidity(); // Trigger validation only when editing starts
        }
      });

      this.editStatusSubscription = formGroup.statusChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(100)
      ).subscribe(() => {
        this.updateValidationErrors(itemID);
      });
      this.updateValidationErrors(itemID);
    }
  }

  updateValidationErrors(itemId: number) {
    const columns = this.getColumnsForEdit() // Override this method in child classes to get the columns for edit
    this.currentValidationErrors = []; // Clear previous errors
    const formGroup = this.editForms[itemId];

    if (formGroup) {
      for (const column of columns) {
        const control = formGroup.get(column.name);
        if (control && control.invalid) {
          const errorKeys = Object.keys(control.errors || {});
          for (const key of errorKeys) {
            const errorMessage = column.errorMessages?.[key];
            if (errorMessage) {
              this.currentValidationErrors.push({
                columnLabel: column.label,
                message: errorMessage
              });
            }
          }
        }
      }
    }
  }



  onEditSave() {
    if (this.editItemID && this.editForms[this.editItemID].valid) {
      this.confirmMessage = `Are you sure you want to save changes?`;
      this.showConfirmDialog = true;
    }
  }

  onEditCancel() {
    if(!this.editItemID) {
      return;
    }
    const formGroup = this.editForms[this.editItemID];
    if(formGroup) {
      const item = this.resultSet?.data.find(item => item.id === this.editItemID);
      if (item) {
        const resetValues: { [key: string]: any } = {};
        this.columns.forEach(column => {
          resetValues[column.name] = this.getValueForInput(item, column);
        });
        formGroup.reset(resetValues);
      }
    }
    this.cleanupEditSubscription();
    this.editItemID = null;
    this.currentValidationErrors = [];
  }

  private cleanupEditSubscription() {
    if (this.editStatusSubscription) {
      this.editStatusSubscription.unsubscribe();
      this.editStatusSubscription = null;
    }
  }

  onDeleteStart(itemID: number) {
    if (itemID) {
      this.deleteItemID = itemID;
      this.confirmMessage = `Are you sure you want to delete?`;
      this.showConfirmDialog = true;
    }
  }

  onNextPage() {
    if (this.currentPage < this.totalPages) {
      this.onPageChange(this.currentPage+1);
    }
  }

  onPrevPage() {
    if (this.currentPage > 1) {
      this.onPageChange(this.currentPage-1);
    }
  }

  onFirstPage() {
    this.onPageChange(1);
  }

  onLastPage() {
    this.onPageChange(this.totalPages);
  }

  onPageChange(newCurrentPage: number) {
    this.pageStateManager.setPageState(this.router.url, newCurrentPage);
    this.currentPage = newCurrentPage;
    this.currentPageSubject.next(this.currentPage);
  }

  getObjectKeys(obj: any): string[] {
    return Object.keys(obj);
  }

  handleConfirm() {
    if(this.editItemID) {
      const item = this.resultSet?.data.find(item => item.id === this.editItemID);
      const updatedItem = { ...item, ...this.editForms[this.editItemID].value };
      this.update(updatedItem);
      this.editItemID = null;
      this.cleanupEditSubscription();
    } else if(this.deleteItemID) {
      this.delete(this.deleteItemID);
      this.deleteItemID = null;
    }
    this.showConfirmDialog = false;
  }

  handleCancel() {
    this.showConfirmDialog = false;
  }

  onPageSizeChange(newPageSize: number) {
    this.pageSize = newPageSize;
    this.pageSizeSubject.next(this.pageSize);
  }



  onSearch(searchTerm: string) {
    this.searchTermSubject.next(searchTerm);
  }

  update(item: T) {
    this.apiService.update(item).subscribe(() => {
      this.notificationService.success(`${this.modelName} updated successfully`);
      this.currentPageSubject.next(this.currentPage)
    });
  }

  onCreate(item: T) {
    this.apiService.create(item).subscribe(() => {
      this.notificationService.success(`${this.modelName} created successfully`);
      this.currentPageSubject.next(this.currentPage)
    });
  }

  delete(itemID: number) {
    this.apiService.delete(itemID).subscribe(() => {
      this.notificationService.success(`${this.modelName} deleted successfully`);
      this.currentPageSubject.next(this.currentPage)
    });
  }



  onDownloadCSV() {
    this.apiService.downloadCSV().subscribe(response => {
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'downloaded-file.csv';

      if (contentDisposition) {
        const matches = /filename="?([^"]+)"?/.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1];
        }
      }

      const blob = new Blob([response.body!], { type: 'text/csv' });

      const link = document.createElement('a');
      const url = window.URL.createObjectURL(blob);
      link.href = url;
      link.download = filename;
      link.click();

      window.URL.revokeObjectURL(url);
    });
  }
}
